import type { ApiPaginatedResponse, ApiResponse, ApiResult } from '@bcube/shared';
import { api } from '@/lib/api';

interface ApiErrorResponse {
  message?: string;
  error?: string;
  field?: string;
}

interface AxiosLikeError {
  response?: {
    data?: ApiErrorResponse;
  };
}

export interface IEconomicGroupCompany {
  name: string;
  taxId: string;
}

export interface IEconomicGroup {
  id: string;
  economic_group_holding?: IEconomicGroupCompany;
  economic_group_controlled?: IEconomicGroupCompany[];
}

export interface FindManyEconomicGroupsParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface FindByTaxIdResponse {
  found: boolean;
  role?: 'holding' | 'controlada';
  economicGroup?: IEconomicGroup;
}

export const economicGroupApi = {
  findMany: async (
    params?: FindManyEconomicGroupsParams,
  ): Promise<PaginatedResponse<IEconomicGroup>> => {
    const queryParams: Record<string, unknown> = {};

    if (params?.page !== undefined) queryParams.page = params.page;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.search) queryParams.search = params.search;

    const { data } = await api.get<ApiPaginatedResponse<IEconomicGroup>>(
      '/commercial/economic-groups',
      { params: queryParams },
    );
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      limit: data.meta.limit,
      totalPages: data.meta.pages,
    };
  },

  create: async (payload: Omit<IEconomicGroup, 'id'>): Promise<ApiResult<{ id: string }>> => {
    try {
      const { data } = await api.post<ApiResponse<{ id: string }>>(
        '/commercial/economic-groups',
        payload,
      );
      return { success: true, data: data.data };
    } catch (error: unknown) {
      const res = (error as AxiosLikeError)?.response?.data;
      return {
        success: false,
        error: {
          global: res?.message || res?.error || 'Erro ao criar grupo econômico',
          field: res?.field,
        },
      };
    }
  },

  update: async (id: string, payload: Partial<Omit<IEconomicGroup, 'id'>>): Promise<ApiResult> => {
    try {
      await api.patch(`/commercial/economic-groups/${id}`, payload);
      return { success: true };
    } catch (error: unknown) {
      const res = (error as AxiosLikeError)?.response?.data;
      return {
        success: false,
        error: {
          global: res?.message || res?.error || 'Erro ao atualizar grupo econômico',
          field: res?.field,
        },
      };
    }
  },

  findByTaxId: async (taxId: string): Promise<FindByTaxIdResponse> => {
    const { data } = await api.get<ApiResponse<FindByTaxIdResponse>>(
      `/commercial/economic-groups/find-by-taxid/${taxId}`,
    );
    return data.data;
  },

  addControlada: async (
    groupId: string,
    controlada: { name: string; taxId: string },
  ): Promise<ApiResult> => {
    try {
      await api.patch(`/commercial/economic-groups/${groupId}/add-controlada`, controlada);
      return { success: true };
    } catch (error: unknown) {
      const res = (error as AxiosLikeError)?.response?.data;
      return {
        success: false,
        error: {
          global: res?.error || 'Erro ao adicionar controlada',
          field: res?.field,
        },
      };
    }
  },

  removeControlada: async (groupId: string, taxId: string): Promise<ApiResult> => {
    try {
      await api.patch(`/commercial/economic-groups/${groupId}/remove-controlada`, { taxId });
      return { success: true };
    } catch (error: unknown) {
      const res = (error as AxiosLikeError)?.response?.data;
      return {
        success: false,
        error: {
          global: res?.error || 'Erro ao remover controlada',
          field: res?.field,
        },
      };
    }
  },

  countLinkedAccounts: async (id: string): Promise<number> => {
    const { data } = await api.get<ApiResponse<{ count: number }>>(
      `/commercial/economic-groups/${id}/linked-accounts-count`,
    );
    return data.data.count;
  },

  delete: async (id: string): Promise<ApiResult> => {
    try {
      await api.delete(`/commercial/economic-groups/${id}`);
      return { success: true };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global:
            (error as AxiosLikeError)?.response?.data?.error || 'Erro ao excluir grupo econômico',
        },
      };
    }
  },
};
